import { Document } from '@contentful/rich-text-types';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { NodeRenderer, Options, documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { ReactNode } from 'react';
import reactStringReplace from 'react-string-replace';
import cn from 'classnames';
import InlineEmbed from './InlineEmbed';
import typeStyles from 'de-sxm-reactlib-2/styles/global/typography.module.scss';
import stringSplit from '@/packages/string/stringSplit';

interface RenderRTEProps {
    contentfulDoc?: Document;
    pToSpan?: boolean;
    removePTag?: boolean;
    largeParagraphFont?: boolean;
    tokens?: {
        [index: string]: ReactNode;
    };
    className?: string;
    customTextSelector?: string;
    customTextStyling?: string;
    customRenderNodes?: Options['renderNode'];
    splitIntoChars?: boolean;
}

export default function RenderRTE({
    contentfulDoc,
    pToSpan,
    removePTag,
    largeParagraphFont,
    tokens,
    className = '',
    customTextSelector,
    customTextStyling,
    customRenderNodes,
    splitIntoChars = false,
}: RenderRTEProps): ReactNode {
    function renderText(children: string): ReactNode {
        if (!children) return children;

        const str = children.toString();
        const anchoridRegEx = /{anchorid=[a-zA-Z0-9-_]+}/g;

        let nodes: ReactNode[] = str.split('\n').flatMap((line, i) => [i > 0 && <br key={`br-${i}`} />, line]);

        const anchorIds = str.match(anchoridRegEx);
        if (anchorIds && anchorIds.length > 0) {
            anchorIds.forEach((anchorId) => {
                const id = anchorId.replace(/{anchorid=|}/g, '');
                nodes = reactStringReplace(nodes, anchorId, () => <span id={id} key={`a-${id}`} />);
            });
        }

        if (tokens) {
            for (const token in tokens) {
                nodes = reactStringReplace(nodes, token, () => tokens[token]);
            }
        }

        if (customTextSelector) {
            nodes = reactStringReplace(nodes, customTextSelector, () => (
                <span className={customTextStyling}>{customTextSelector}</span>
            ));
        }

        if (splitIntoChars) {
            nodes = nodes.flatMap((node) => {
                if (typeof node === 'string') {
                    return stringSplit({ str: node });
                }
                return node;
            }) as ReactNode[];
        }

        return nodes;
    }

    const renderParagraph: NodeRenderer = (node, children) => {
        const classNames = cn(typeStyles.paragraph, className, {
            [typeStyles['text-xxlarge']]: largeParagraphFont,
        });

        if (children?.toString().replace(/\s/g, '')) {
            if (pToSpan) {
                return <span className={classNames}>{children}</span>;
            } else if (removePTag) {
                return children;
            } else {
                return <p className={className}>{children}</p>;
            }
        }

        return children;
    };

    if (contentfulDoc) {
        const options: Options = {
            renderText: renderText,
            renderNode: {
                [BLOCKS.HEADING_1]: (node, children) => (
                    <span className={`${typeStyles.h1} ${className}`}>{children}</span>
                ),
                [BLOCKS.HEADING_2]: (node, children) => (
                    <span className={`${typeStyles.h2} ${className}`}>{children}</span>
                ),
                [BLOCKS.HEADING_3]: (node, children) => (
                    <span className={`${typeStyles.h3} ${className}`}>{children}</span>
                ),
                [BLOCKS.HEADING_4]: (node, children) => (
                    <span className={`${typeStyles.h4} ${className}`}>{children}</span>
                ),
                [BLOCKS.HEADING_5]: (node, children) => (
                    <span className={`${typeStyles.h5} ${className}`}>{children}</span>
                ),
                [BLOCKS.HEADING_6]: (node, children) => (
                    <span className={`${typeStyles.h6} ${className}`}>{children}</span>
                ),
                [BLOCKS.PARAGRAPH]: renderParagraph,
                [BLOCKS.TABLE]: (node, children) => (
                    <table className="rtetable">
                        <tbody>{children}</tbody>
                    </table>
                ),
                [INLINES.EMBEDDED_ENTRY]: (node) => <InlineEmbed node={node} />,
                ...customRenderNodes,
            },
        };

        return documentToReactComponents(contentfulDoc, options);
    }

    return undefined;
}
